require('./RAExpectedProfitability.scss');

// @vendor
const PropTypes = require('prop-types');
const React = require('react');
const classnames = require('classnames');
const { isEmpty } = require('lodash');

// @components
const RAExpectedProfitabilityFooter = require('./RAExpectedProfitabilityFooter');
const RAProjectionChart = require('../RAProjectionChart/RAProjectionChart');
const RAPillButtons = require('../RAPillButtons/RAPillButtons');
const RAProbabilityTag = require('../RAProbabilityTag/RAProbabilityTag');

// @commons
const { EmptyState } = require('commonsComponents/emptyState');
const { LoadingSection } = require('commonsComponents/loadingSection');
const { OKButton } = require('components/commons/OKButton/OKButton');
const RACircularGoalIcon = require('../RACircularGoalIcon/RACircularGoalIcon');

// @utilities
const { formatText, FormattedText } = require('core/i18n').i18n;

// @constants
const {
    RA_CIRCULAR_GOAL_ICON_SUCCESS,
    RA_CIRCULAR_GOAL_ICON_WARNING,
    RA_DISASSOCIATE_GOAL_URL,
} = require('../../raConstants/index');

class RAExpectedProfitability extends React.Component {
    constructor(props) {
        super(props);

        this.formatter = this.formatter.bind(this);
    }

    formatter(value) {
        return (
            <FormattedText
                value="rentabilityGraph-nYears"
                injectedStrings={[value]}
            />
        );
    }

    getHeaderSection() {
        const {
            changeYears,
            chartButtons,
            chartYears,
            disabled,
            goalReached,
            isGoalNotReached,
            isObjectiveTracking,
            title,
            headerMarginBottom,
            subtitle,
            titleTooltip,
            probability,
        } = this.props;
        const headerClasses = classnames('ra-expected-profitability__header', {
            'ra-expected-profitability__header--margin-bottom-none': headerMarginBottom,
        });
        const subtitleClasses = classnames(
            'ra-expected-profitability__subtitle',
            {
                'ra-expected-profitability__subtitle--smaller-font': isObjectiveTracking,
            },
        );
        const titleElement = !!title && (
            <div className="ra-expected-profitability__header-texts">
                <h2 className="ra-expected-profitability__title">
                    {title}
                    {titleTooltip}
                </h2>
            </div>
        );
        const chartButtonsElement = !!chartButtons && (
            <div className="ra-expected-profitability__buttons">
                <RAPillButtons
                    changeOption={changeYears}
                    selectedOption={chartYears}
                    buttons={chartButtons}
                    formatter={this.formatter}
                    disabled={disabled}
                />
            </div>
        );
        const probabilityElement = !!probability && (
            <span className="ra-expected-profitability__probability-tag">
                <RAProbabilityTag label={probability} />
            </span>
        );
        const subtitleElement = !isGoalNotReached &&
            !goalReached &&
            !!subtitle && (
                <h5 className={subtitleClasses}>
                    {subtitle}
                    {probabilityElement}
                </h5>
            );
        return (
            <div>
                <div className={headerClasses}>
                    {titleElement}
                    {chartButtonsElement}
                </div>
                {subtitleElement}
            </div>
        );
    }

    buildEmptyGoalSection(isGoalNotReached) {
        const { immPortfolio } = this.props;
        const portfolioId = immPortfolio.get('id');
        const goalType = immPortfolio.getIn(['goalInformation', 'goalType']);
        const portfolioIsCancelled = immPortfolio.getIn(['state', 'cancelled']);
        const disassociateGoalURL = `${RA_DISASSOCIATE_GOAL_URL}/${portfolioId}`;
        const title = isGoalNotReached
            ? 'raObjectiveTracking-raObjectiveTracking_goalNotReachedTitle'
            : 'raObjectiveTracking-raObjectiveTracking_goalReachedTitle';
        const subtitle =
            'raObjectiveTracking-raObjectiveTracking_goalReachedSubtitle';
        const iconModifier = isGoalNotReached
            ? RA_CIRCULAR_GOAL_ICON_WARNING
            : RA_CIRCULAR_GOAL_ICON_SUCCESS;
        const button = (
            <OKButton
                modifier="primary"
                to={disassociateGoalURL}
                disabled={portfolioIsCancelled}
            >
                <FormattedText value="raObjectiveTracking-raObjectiveTracking_goalReachedConvertButton" />
            </OKButton>
        );
        return (
            <div className="ra-expected-profitability__projection-reached-section">
                <div className="ra-expected-profitability__projection-reached-section-icon">
                    <RACircularGoalIcon
                        type={goalType}
                        modifier={iconModifier}
                    />
                </div>
                <div className="ra-expected-profitability__projection-reached-section-title">
                    <FormattedText value={title} />
                </div>
                <div className="ra-expected-profitability__projection-reached-section-subtitle">
                    <FormattedText value={subtitle} />
                </div>
                <div className="ra-expected-profitability__projection-reached-section-button">
                    {button}
                </div>
            </div>
        );
    }

    getChartSection() {
        const {
            chartYears,
            chartData,
            disabled,
            error,
            goalReached,
            loading,
            isObjectiveTracking,
            tickInfo,
            objAmount,
            isGoalNotReached,
            actualAmount,
        } = this.props;

        let content;

        if (goalReached) {
            content = this.buildEmptyGoalSection();
        } else if (loading) {
            content = <LoadingSection />;
        } else if (isGoalNotReached) {
            content = this.buildEmptyGoalSection(isGoalNotReached);
        } else {
            if (error || isEmpty(chartData) || chartData.length < 2) {
                let message =
                    'raObjectiveTracking-raObjectiveTracking_noResult';
                let iconType = 'search-wealth';
                if (error) {
                    message =
                        'raObjectiveTracking-raObjectiveTracking_apiFailure';
                    iconType = 'nointernet-wealth';
                }
                content = (
                    <EmptyState
                        primaryText={formatText(message)}
                        iconType={iconType}
                    />
                );
            } else {
                content = (
                    <RAProjectionChart
                        isObjectiveTracking={isObjectiveTracking}
                        data={chartData}
                        years={chartYears}
                        loading={loading}
                        disabled={disabled}
                        tickInfo={tickInfo}
                        objAmount={objAmount}
                        actualAmount={actualAmount}
                    />
                );
            }
        }
        return content;
    }

    render() {
        const { disabled, pgSection, showFooter } = this.props;

        const topClassName = classnames('ra-expected-profitability', {
            'ra-expected-profitability--disabled': disabled,
        });
        const headerSection = this.getHeaderSection();
        const footerElement = showFooter && (
            <RAExpectedProfitabilityFooter pgSection={pgSection} />
        );
        const chartSection = this.getChartSection();

        return (
            <div className={topClassName}>
                {headerSection}
                {chartSection}
                {footerElement}
            </div>
        );
    }
}

RAExpectedProfitability.propTypes = {
    actualAmount: PropTypes.number,
    changeYears: PropTypes.func,
    chartButtons: PropTypes.array,
    chartData: PropTypes.array.isRequired,
    chartYears: PropTypes.number.isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    goalReached: PropTypes.bool,
    goalType: PropTypes.string,
    headerMarginBottom: PropTypes.bool,
    immPortfolio: PropTypes.object,
    isObjectiveTracking: PropTypes.bool,
    loading: PropTypes.bool,
    objAmount: PropTypes.number,
    isGoalNotReached: PropTypes.bool,
    pgSection: PropTypes.bool,
    probability: PropTypes.string,
    showFooter: PropTypes.bool,
    subtitle: PropTypes.string,
    tickInfo: PropTypes.object,
    title: PropTypes.string,
    titleTooltip: PropTypes.object,
};

RAExpectedProfitability.defaultProps = {
    changeYears: () => {},
    chartButtons: null,
    disabled: false,
    goalReached: false,
    headerMarginBottom: false,
    isObjectiveTracking: false,
    isGoalNotReached: false,
    pgSection: false,
    showFooter: true,
    error: false,
};

module.exports = RAExpectedProfitability;
